import React, { useContext, useState } from 'react';
import { useMutation } from '@apollo/client';
import { MenuItem, TextField, Tooltip } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { css } from '@emotion/react';
import { Body, GreyBody, OnwardSwitch, DebouncedTextField, OnwardTooltip, PrimaryButton } from '@/styles/blocks';
import { UserContext } from '@/components/App';
import { CorpAdminContext } from '../store';
import { UPDATE_CORP_PRICING_OVERRIDE } from '../graphql';
import { greens, greys, reds } from '@/styles/colors';
import { FUEL_TYPE_OPTIONS, MARKETPLACE_TIER_LABELS, SUPPLY_CHAIN_LEG_LABELS } from './constants';
import { ALGO_TYPES } from '@/components/Account/Tariffs/constants';
import { PricingBlock, FieldRow, FieldGroup, EditActions, CopyableId } from './blocks';

const algoLabel = (algo_type) =>
    ALGO_TYPES[algo_type] ||
    (algo_type
        ? algo_type
              .replace(/_/g, ' ')
              .toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase())
        : '--');

const PricingRow = ({ pricing, shipper_id, side, forContractor = false }) => {
    const isShipperSide = side === 'shipper';
    const { admin_clients } = useContext(UserContext);
    const { state, callbacks } = useContext(CorpAdminContext);
    const { tariffs } = state;
    const { setNotification } = callbacks;

    const shipper = (admin_clients || []).find((c) => c.client_id === shipper_id);
    const shipperType = shipper?.shipper_type || 'MARKETPLACE';
    const carrierIdForAssignment = pricing.partner_client_id || pricing.assign_id;
    const canRateShop = shipperType === 'RATE_SHOP' && !!carrierIdForAssignment;
    const currentAssignment = pricing.assign_id ? 'rate_shop' : 'auto';

    const [draft, setDraft] = useState({
        enabled: pricing.enabled,
        zip_geofence: pricing.zip_geofence,
        add_fuel_surcharge: pricing.add_fuel_surcharge,
        fuel_type: pricing.fuel_type,
        add_margin_flat: pricing.add_margin_flat,
        add_margin_rate: pricing.add_margin_rate,
        tariff_id: pricing.tariff_id,
        algo_type: pricing.algo_type,
        marketplace_tier: pricing.marketplace_tier,
    });

    const applyDraft = (updates) => setDraft((prev) => ({ ...prev, ...updates }));

    const ownerOf = (t) => (admin_clients || []).find((c) => c.client_id === t.client_id);
    const isShipperOwned = (t) => !!ownerOf(t)?.user?.roles?.['SHIPPER'];
    // A contractor may be paid on any shipper's settlement tariff, but never on another Flex Center's.
    const tariffMatchesSide = (t) => !t.client_id || t.client_id === shipper_id || (forContractor && isShipperOwned(t));
    const tariffsByAlgoType = tariffs.reduce((acc, t) => {
        if (!tariffMatchesSide(t)) return acc;

        const key = t.algo_type || 'Other';
        (acc[key] ??= []).push(t);

        return acc;
    }, {});

    const tariffLabel = (t) => t.name || 'Tariff';

    const tariffSourceLabel = (t) => {
        if (!t.client_id) return 'Org-level (shared)';

        const ownerRole = isShipperOwned(t) ? 'Shipper' : 'Flex Center';
        return `${ownerRole}: ${ownerOf(t)?.business_name || 'Account'}`;
    };

    // Flattened in group order: Autocomplete's groupBy only draws a header where the group changes.
    const tariffOptions = Object.values(tariffsByAlgoType).flat();
    const listedTariff = tariffs.find((tariff) => tariff.tariff_id === draft.tariff_id);
    const unlistedTariff = draft.tariff_id ? { tariff_id: draft.tariff_id, name: null } : null;
    const selectedTariff = listedTariff || unlistedTariff;
    const filterTariffsByNameOrOwner = createFilterOptions({
        stringify: (t) => `${tariffLabel(t)} ${tariffSourceLabel(t)}`,
    });

    const [update, { loading: saving }] = useMutation(UPDATE_CORP_PRICING_OVERRIDE, {
        onCompleted: () => setNotification({ severity: 'success', message: 'Pricing saved.' }),
        onError: (err) => setNotification({ severity: 'error', message: err.message }),
    });

    const handleAssignmentChange = (e) => {
        const next = e.target.value;
        if (next === currentAssignment) return;
        const patch =
            next === 'rate_shop'
                ? {
                      partner_client_id: null,
                      assign_id: carrierIdForAssignment,
                  }
                : {
                      partner_client_id: carrierIdForAssignment,
                      assign_id: null,
                  };
        update({ variables: { pricing_override_id: pricing.pricing_override_id, patch } });
    };

    const handleSave = () => {
        update({
            variables: {
                pricing_override_id: pricing.pricing_override_id,
                patch: draft,
            },
        });
    };

    const isDisabled = !draft.enabled;
    const isOrgDefault = !!pricing.organization_id;
    // An enabled default without a tariff outranks the marketplace rate and blanks carrier pay.
    const missingDefaultTariff = isOrgDefault && draft.enabled && !draft.tariff_id;

    const dimmedCss = isDisabled
        ? css`
              opacity: 0.45;
          `
        : null;

    return (
        <PricingBlock
            css={css`
                ${isDisabled &&
                css`
                    background-color: ${greys.selected};
                    border-color: ${greys.primary};
                    border-style: dashed;
                `}
            `}
        >
            {/* Row 1: IDs, algo type, assignment type, parameters */}
            <FieldRow>
                <CopyableId label="Pricing ID" value={pricing.pricing_override_id} />
                {isDisabled && (
                    <span
                        css={css`
                            display: inline-flex;
                            align-items: center;
                            padding: 0.15rem 0.5rem;
                            border-radius: 4px;
                            background: ${reds.primary};
                            color: #fff;
                            font-size: 11px;
                            font-weight: 700;
                            letter-spacing: 0.05em;
                        `}
                    >
                        DISABLED
                    </span>
                )}
                <FieldRow
                    css={css`
                        gap: 0.25rem;
                        margin-bottom: 0;
                        align-items: center;
                    `}
                >
                    <GreyBody
                        css={css`
                            margin-bottom: 0;
                        `}
                    >
                        Leg:
                    </GreyBody>
                    <Body>
                        {pricing.override_type
                            ? SUPPLY_CHAIN_LEG_LABELS[pricing.override_type] || pricing.override_type
                            : '--'}
                    </Body>
                </FieldRow>
                <FieldRow
                    css={css`
                        gap: 0.25rem;
                        margin-bottom: 0;
                        align-items: center;
                    `}
                >
                    <GreyBody
                        css={css`
                            margin-bottom: 0;
                        `}
                    >
                        Algo Type:
                    </GreyBody>
                    <Body>{algoLabel(draft.algo_type)}</Body>
                </FieldRow>
                <FieldRow
                    css={css`
                        gap: 0.25rem;
                        margin-bottom: 0;
                        align-items: center;
                    `}
                >
                    <GreyBody
                        css={css`
                            margin-bottom: 0;
                        `}
                    >
                        Tier:
                    </GreyBody>
                    <Body>{MARKETPLACE_TIER_LABELS[pricing.marketplace_tier] || 'Unknown'}</Body>
                </FieldRow>
                {isShipperSide && (
                    <Tooltip
                        title={
                            !canRateShop && currentAssignment !== 'rate_shop'
                                ? 'Rate Shop only available when shipper type is RATE_SHOP'
                                : ''
                        }
                        placement="top"
                    >
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            label="Assignment Type"
                            value={currentAssignment}
                            onChange={handleAssignmentChange}
                            disabled={saving || !carrierIdForAssignment}
                            helperText="Saves immediately"
                            css={css`
                                min-width: 180px;
                            `}
                        >
                            <MenuItem value="auto">Auto Assign</MenuItem>
                            <MenuItem value="rate_shop" disabled={!canRateShop}>
                                Rate Shop
                            </MenuItem>
                        </TextField>
                    </Tooltip>
                )}
            </FieldRow>

            {/* Row 2: Enabled, tariff, margins */}
            <FieldRow
                css={css`
                    margin-top: 0.75rem;
                `}
            >
                <FieldGroup>
                    <OnwardSwitch
                        label="Enabled"
                        checked={!!draft.enabled}
                        onChange={(e) => applyDraft({ enabled: e.target.checked })}
                    />
                </FieldGroup>
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        flex-wrap: wrap;
                        gap: 1rem;
                        flex: 1;
                        ${dimmedCss};
                    `}
                >
                    <Autocomplete
                        options={tariffOptions}
                        value={selectedTariff}
                        onChange={(e, tariff) =>
                            applyDraft({ tariff_id: tariff?.tariff_id ?? null, algo_type: tariff?.algo_type })
                        }
                        groupBy={(t) => algoLabel(t.algo_type || 'Other')}
                        getOptionLabel={tariffLabel}
                        getOptionSelected={(option, value) => option.tariff_id === value.tariff_id}
                        filterOptions={filterTariffsByNameOrOwner}
                        disableClearable={isOrgDefault}
                        renderOption={(t) => (
                            <div
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                `}
                            >
                                <span>{tariffLabel(t)}</span>
                                <span
                                    css={css`
                                        font-size: 11px;
                                        color: ${greys.primary};
                                    `}
                                >
                                    {tariffSourceLabel(t)}
                                </span>
                            </div>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} label="Tariff" size="small" variant="outlined" placeholder="None" />
                        )}
                        css={css`
                            width: 300px;
                        `}
                    />
                    <DebouncedTextField
                        label="Margin Flat"
                        size="small"
                        variant="outlined"
                        value={draft.add_margin_flat ?? ''}
                        onChange={(e) => {
                            const parsed = parseFloat(e.target.value);
                            applyDraft({ add_margin_flat: isNaN(parsed) ? null : parsed });
                        }}
                        InputProps={{ startAdornment: <span>$</span> }}
                        css={css`
                            width: 110px;
                        `}
                    />
                    <DebouncedTextField
                        label="Margin Rate"
                        size="small"
                        variant="outlined"
                        value={draft.add_margin_rate ?? ''}
                        onChange={(e) => {
                            const parsed = parseFloat(e.target.value);
                            applyDraft({ add_margin_rate: isNaN(parsed) ? null : parsed });
                        }}
                        InputProps={{ endAdornment: <span>%</span> }}
                        css={css`
                            width: 110px;
                        `}
                    />
                </div>
            </FieldRow>

            {/* Row 3: Zip filter, fuel surcharge, fuel type, parameters */}
            <FieldRow
                css={css`
                    margin-top: 0.75rem;
                    ${dimmedCss};
                `}
            >
                <FieldGroup>
                    <OnwardSwitch
                        label="Zip Filter"
                        checked={!!draft.zip_geofence}
                        onChange={(e) => applyDraft({ zip_geofence: e.target.checked })}
                    />
                </FieldGroup>
                <FieldGroup>
                    <OnwardSwitch
                        label="Fuel Surcharge"
                        checked={!!draft.add_fuel_surcharge}
                        onChange={(e) => applyDraft({ add_fuel_surcharge: e.target.checked })}
                    />
                </FieldGroup>
                <FieldGroup>
                    <TextField
                        select
                        label="Fuel Type"
                        size="small"
                        variant="outlined"
                        value={draft.fuel_type || ''}
                        onChange={(e) => applyDraft({ fuel_type: e.target.value })}
                        css={css`
                            min-width: 120px;
                        `}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {FUEL_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </FieldGroup>
                {pricing.pricing_parameters && (
                    <OnwardTooltip title={JSON.stringify(pricing.pricing_parameters, null, 2)} placement="top">
                        <Body
                            css={css`
                                cursor: help;
                                color: ${greens.primary};
                                text-decoration: underline;
                                font-weight: 600;
                            `}
                        >
                            Parameters
                        </Body>
                    </OnwardTooltip>
                )}
            </FieldRow>

            <EditActions>
                {isShipperSide && (
                    <GreyBody
                        css={css`
                            margin-bottom: 0;
                            margin-right: auto;
                            font-size: 12px;
                        `}
                    >
                        Assignment Type saves instantly. Other changes apply when you click Save.
                    </GreyBody>
                )}
                <PrimaryButton loading={saving} disabled={missingDefaultTariff} onClick={handleSave}>
                    Save
                </PrimaryButton>
            </EditActions>
        </PricingBlock>
    );
};

export default PricingRow;
